import { Modal } from '@/components/common/AppModal';
import { Card, Form, Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import SimpleBarReact from 'simplebar-react';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import TextareaAutosize from 'react-textarea-autosize';
import Picker from '@emoji-mart/react';
import { useAppContext } from '@/Main';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { IoSend } from 'react-icons/io5';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { hideDrawer } from '@/app/common/reducers/drawer';
import Flex from '@/components/common/Flex';
import Avatar from '@/components/common/Avatar';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import {
  useCreateKeywordNoteMutation,
  useDeleteKeywordNoteMutation,
  useGetKeywordNotesQuery,
  useUpdateKeywordNoteMutation
} from '@/app/KeywordTracker/api/keywordsApi';
import { DateObject } from 'react-multi-date-picker';
import { Formik } from 'formik';
import { errorMessage, successMessage } from '@/utils/toast';
import CancelModal from '@/app/common/components/CancelModal';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import { darkenColor, getColor, hexToRgb } from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
import NoteItems from './NoteItems';
import type { EditingNote } from './NoteItems';

const NoteModal = () => {
  const { data: userInfo } = useGetUserInfoQuery();
  const { domain_slug } = useDomain();
  const {
    config: { isRTL, isDark }
  } = useAppContext();
  const { key, payload } = useSelector(state => state.drawer);
  const { keyword_id, keyword } = payload ?? '';

  const {
    data: notesResult,
    isError,
    isLoading,
    isFetching
  } = useGetKeywordNotesQuery(
    {
      keyword_id
    },
    { skip: !keyword || key !== 'keyword_notes' }
  );
  const [updateNote, { isLoading: isEditingNote }] =
    useUpdateKeywordNoteMutation();
  const [deleteNote] = useDeleteKeywordNoteMutation();
  // FOR DELETE NOTE
  const [deletingNote, setDeletingNote] = useState<EditingNote | null>(null);
  // FOR REPLY NOTE
  const [replyingMessage, setReplyingMessage] = useState<EditingNote | null>(null);
  // --------------------
  const [edit, setEdit] = useState<EditingNote | null>(null);
  const [sendNote, { isLoading: isSendingNote }] =
    useCreateKeywordNoteMutation();
  const dispatch = useDispatch();
  const notes = notesResult?.data ?? [];
  const [previewEmoji, setPreviewEmoji] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!edit) {
      scrollToBottom();
    }
  }, [notesResult]);
  const modalRef = useRef<HTMLDivElement>(null);

  const [size, setSize] = useState({ width: 500, height: 600 });

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!modalRef.current) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = modalRef.current.offsetWidth;
    const startHeight = modalRef.current.offsetHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = startWidth + (moveEvent.clientX - startX);
      const newHeight = startHeight + (moveEvent.clientY - startY);
      setSize({
        width: Math.max(500, newWidth),
        height: Math.max(300, newHeight)
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  const [t] = useTranslation();
  return (
    <Modal
      dialogClassName="note-modal-dialog"
      contentClassName="note-modal"
      // contentClassName="note-modal-height"
      onHide={() => {
        dispatch(hideDrawer());
      }}
      show={key === 'keyword_notes'}
    >
      <div
        ref={modalRef}
        style={{ width: size.width, height: size.height, position: 'relative' }}
      >
        <Card
          className="overflow-hidden rounded-4 d-flex flex-column h-100"
          style={{
            height: '100% !important'
          }}
        >
          <FalconCardHeader
            title={
              <div className="position-relative d-flex align-items-center">
                <div className="d-flex flex-1 gap-2 align-items-center">
                  <Avatar
                    name={
                      <FontAwesomeIcon
                        icon="comment"
                        size="lg"
                        className="text-primary"
                      />
                    }
                    isExact
                    size="xl"
                    className="border border-3 rounded-circle border-200"
                  />
                  یادداشت{' '}
                  <strong className="fw-bold">
                    ({decodeURIComponent(keyword)})
                  </strong>
                </div>

                <FalconCloseButton onClick={() => dispatch(hideDrawer())} />
              </div>
            }
            titleTag="h5"
            className="py-3"
            light
          />
          <CancelModal
            show={deletingNote}
            setShow={() => setDeletingNote(false)}
            title="حذف یادداشت"
            desc={
              <span>
                <Form.Label> آیا از حذف این یادداشت مطمئن هستی؟</Form.Label>

                <Form.Control
                  disabled
                  rows="auto"
                  as="textarea"
                  value={deletingNote?.description ?? ''}
                />
              </span>
            }
            submitTitle="بله، حذف کن"
            submitFunc={() => {
              if (deletingNote.id)
                deleteNote({ id: deletingNote?.id })
                  .unwrap()
                  .then(() => {
                    successMessage('یادداشت با موفقیت حذف شد.');
                    setDeletingNote(null);
                  })
                  .catch(err => {
                    errorMessage('حذف یادداشت با خطا مواجه شد.');
                    console.error(err);
                  });
            }}
          />
          <style>{`
        .chat-editor-area .message-input {
          height: 100% !important;
          min-height: 100% !important;
        }
      `}</style>
          <Formik
            onSubmit={(values, actions) => {
              const noteText = values.note.trim();
              if (edit?.id) {
                updateNote({
                  id: edit.id,
                  description: noteText,
                  is_public: edit.is_public
                }).then(() => {
                  successMessage('یادداشت ویرایش شد.');
                  setEdit(null);
                  actions.resetForm();
                });
              } else {
                sendNote({
                  keyword_id,
                  domain_slug,
                  parent_id: replyingMessage?.id ?? null,
                  description: noteText,
                  is_public: true
                }).then(() => {
                  setReplyingMessage(null);
                  successMessage('یادداشت ارسال شد.');
                  actions.resetForm();
                });
              }
            }}
            validationSchema={yup.object().shape({
              note: yup
                .string()
                .trim()
                .required('یادداشت رو وارد نکردید.')
                .max(500, 'حداکثر ۵۰۰ کاراکتر مجاز است.')
            })}
            initialValues={{
              note: ''
            }}
          >
            {({
              values,
              setFieldValue,
              handleChange,
              handleSubmit,
              errors
            }) => (
              <>
                <Card.Body as={Flex} className="p-0 flex-1">
                  <SimpleBarReact
                    style={{
                      height: replyingMessage
                        ? size.height - 170
                        : size.height - 150,
                      width: '100%'
                      // margin: '0 auto'
                    }}
                  >
                    <NoteItems
                      notes={notes}
                      edit={edit}
                      setEdit={setEdit}
                      isLoading={isLoading}
                      replyingMessage={replyingMessage}
                      setReplyingMessage={setReplyingMessage}
                      updateNote={updateNote}
                      setDeletingNote={setDeletingNote}
                    />
                    <div ref={messagesEndRef} />
                  </SimpleBarReact>
                </Card.Body>
                {/* SENDER FORM */}

                <Form.Control.Feedback
                  type={errors.note ? 'invalid' : 'valid'}
                  className="d-block my-1 ps-1"
                >
                  &nbsp;{errors.note && errors.note}
                </Form.Control.Feedback>
                {replyingMessage && (
                  <div className="px-2 pt-1 border-top">
                    <div className="bg-200 d-flex gap-1 rounded-2 fs--2 mb-1 border-primary border-start p-1">
                      <FontAwesomeIcon icon="reply" />
                      <span className="flex-1" style={{ marginTop: '-2px' }}>
                        {replyingMessage?.description}
                      </span>

                      <FontAwesomeIcon
                        onClick={() => setReplyingMessage(null)}
                        className="cursor-pointer"
                        icon="times"
                      />
                    </div>
                  </div>
                )}

                <Form
                  dir="ltr"
                  style={{ maxHeight: '600px' }}
                  className={`mt-auto chat-editor-area m-auto fs-2`}
                  onSubmit={e => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div dir="rtl" className="d-flex w-100">
                    <Button
                      variant="link"
                      className="emoji-icon d-flex items-center justify-center px-3 py-2 m-0 align-self-end mb-2 border-0"
                      onClick={() => setPreviewEmoji(!previewEmoji)}
                    >
                      <FontAwesomeIcon
                        className="fs-2 mx-2 border-0"
                        icon={!previewEmoji ? 'face-smile' : 'times'}
                        onClick={() => setPreviewEmoji(!previewEmoji)}
                      />
                    </Button>

                    {previewEmoji && (
                      <div
                        className="note-emoji-picker"
                        dir="rtl"
                        style={{
                          fontFamily: 'dana !important'
                        }}
                      >
                        <Picker
                          locale="fa"
                          set="google"
                          onEmojiSelect={e => {
                            let emoji = e.native;
                            setFieldValue('note', values.note + emoji);
                            setPreviewEmoji(false);
                          }}
                          theme={isDark ? 'dark' : 'light'}
                          previewPosition="none"
                          skinTonePosition="none"
                        />
                      </div>
                    )}
                    <TextareaAutosize
                      dir={isRTL ? 'rtl' : 'ltr'}
                      minRows={1}
                      maxRows={6}
                      name="note"
                      id="note"
                      value={values.note}
                      placeholder="یادداشت خود را وارد کنید"
                      onChange={handleChange}
                      className="message-input form-control outline-none resize-none rounded-0 border-0 emojiarea-editor px-2 py-3"
                      style={{ fontSize: '16px' }}
                    />

                    {edit && (
                      <Button
                        size="lg"
                        variant="danger"
                        className="m-2 ms-0 d-flex align-items-center justify-content-center align-self-end "
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          padding: 0
                        }}
                        onClick={() => {
                          setEdit(null);
                          setFieldValue('note', '');
                        }}
                      >
                        <FontAwesomeIcon icon="times" color="white" />
                      </Button>
                    )}
                    <Button
                      variant={edit ? 'success' : 'falcon-primary'}
                      size="lg"
                      type="submit"
                      className="m-2 d-flex align-items-center justify-content-center align-self-end "
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        padding: 0,
                        transform: !edit ? 'scale(-1,-1)' : ''
                      }}
                      disabled={isSendingNote || isEditingNote}
                    >
                      {isSendingNote || isEditingNote ? (
                        <Spinner
                          style={{
                            width: '20px',
                            height: '20px'
                          }}
                        />
                      ) : edit ? (
                        <FontAwesomeIcon icon="check" color="white" />
                      ) : (
                        <IoSend
                          style={{
                            fontSize: '1.2rem',
                            marginRight: '-3px',
                            color: 'white',
                            width: '40px'
                          }}
                        />
                      )}
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </Card>
        <div
          className="allaho_akbar_zIndex custom-gutter"
          onMouseDown={startResize}
        />
      </div>
    </Modal>
  );
};

export default NoteModal;
